import { useState, useEffect, useCallback } from 'react';
import { Chart } from './Chart';
import { ChartEditorSidebar } from './ChartEditorSidebar';
import type { Analysis } from '../services/analysis.service';
import ApiService from '../services/api.service';
import { Button } from './ui/button';

interface DataExplorerProps {
  analysis: Analysis;
  originalAnalysisId: string;
  dateRange?: string;
  customDateStart?: string;
  customDateEnd?: string;
  onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
  onAnalysisChange?: (updatedAnalysis: Analysis) => void;
  onDrillDown?: (analysis: Analysis, dateRange?: string, customDateStart?: string, customDateEnd?: string) => void;
  onClose?: () => void;
  autoApply?: boolean;
}

export function DataExplorer({
  analysis,
  originalAnalysisId,
  dateRange: dateRangeProp,
  customDateStart: customDateStartProp,
  customDateEnd: customDateEndProp,
  onDateRangeChange: onDateRangeChangeProp,
  onAnalysisChange,
  onDrillDown: onDrillDownProp,
  onClose,
  autoApply = false,
}: DataExplorerProps) {
  // Track two separate analysis states:
  // - appliedAnalysis: used for data fetching (only updates on Apply/Refresh)
  // - pendingAnalysis: shown in sidebar UI (updates immediately on any change)
  const [appliedAnalysis, setAppliedAnalysis] = useState<Analysis>(analysis);
  const [pendingAnalysis, setPendingAnalysis] = useState<Analysis>(analysis);
  const [isApplying, setIsApplying] = useState(autoApply);
  const [hasAppliedOnce, setHasAppliedOnce] = useState(autoApply);

  // Manage date range locally so the picker always works,
  // even when the caller doesn't provide onDateRangeChange.
  // Fall back to the analysis object's date range fields if no explicit props given.
  const [localDateRange, setLocalDateRange] = useState<string>(
    dateRangeProp || analysis.date_range?.toLowerCase() || 'all_time'
  );
  const [localCustomDateStart, setLocalCustomDateStart] = useState<string>(
    customDateStartProp || analysis.custom_date_range_start?.slice(0, 10) || ''
  );
  const [localCustomDateEnd, setLocalCustomDateEnd] = useState<string>(
    customDateEndProp || analysis.custom_date_range_end?.slice(0, 10) || ''
  );

  // Sync from props when they change externally
  useEffect(() => {
    if (dateRangeProp !== undefined) setLocalDateRange(dateRangeProp);
  }, [dateRangeProp]);
  useEffect(() => {
    if (customDateStartProp !== undefined) setLocalCustomDateStart(customDateStartProp);
  }, [customDateStartProp]);
  useEffect(() => {
    if (customDateEndProp !== undefined) setLocalCustomDateEnd(customDateEndProp);
  }, [customDateEndProp]);

  const handleDateRangeChange = useCallback((dateRange: string, customStart?: string, customEnd?: string) => {
    setLocalDateRange(dateRange);
    setLocalCustomDateStart(customStart || '');
    setLocalCustomDateEnd(customEnd || '');
    // Also notify parent if callback is provided
    if (onDateRangeChangeProp) {
      onDateRangeChangeProp(dateRange, customStart, customEnd);
    }
  }, [onDateRangeChangeProp]);

  // Determine if we should show the chart:
  // - If it's an existing analysis (not 'new'), always show it
  // - If it's a new analysis, only show after user has applied at least once
  const isNewAnalysis = !originalAnalysisId;
  const shouldShowChart = !isNewAnalysis || hasAppliedOnce;

  // Sync both analysis states when analysis prop changes
  useEffect(() => {
    setAppliedAnalysis(analysis);
    setPendingAnalysis(analysis);
  }, [analysis]);

  const handleApply = (updatedAnalysis: Analysis, isApplying?: boolean) => {
    setPendingAnalysis(updatedAnalysis);
    if (isApplying) {
      // When applying, update the applied analysis to trigger data refresh
      setAppliedAnalysis(updatedAnalysis);
      setIsApplying(true);
      // Mark that user has applied at least once
      setHasAppliedOnce(true);
    }
    if (onAnalysisChange) {
      onAnalysisChange(updatedAnalysis);
    }
  };

  // Handle drilldown from chart — if parent provides onDrillDown (tab context),
  // delegate to it so a new tab is created. Otherwise apply in-place.
  const handleDrillDown = (
    updatedAnalysis: Analysis,
    drillDateRange?: string,
    drillCustomStart?: string,
    drillCustomEnd?: string,
  ) => {
    if (onDrillDownProp) {
      onDrillDownProp(updatedAnalysis, drillDateRange, drillCustomStart, drillCustomEnd);
      return;
    }
    if (drillDateRange) {
      handleDateRangeChange(drillDateRange, drillCustomStart, drillCustomEnd);
    }
    handleApply(updatedAnalysis, true);
  };

  // Handle adding filter without refresh - just updates the sidebar
  const handleDimensionFilterNoRefresh = (updatedAnalysis: Analysis) => {
    // Only update pendingAnalysis - don't update appliedAnalysis
    // This shows the filter in the sidebar without triggering a data reload
    setPendingAnalysis(updatedAnalysis);
    if (onAnalysisChange) {
      onAnalysisChange(updatedAnalysis);
    }
  };

  return (
    <div className="h-full w-full flex overflow-hidden">
      {/* Sidebar - uses pendingAnalysis to show pending changes */}
      <div className="w-[20%] min-w-[250px] flex-shrink-0">
        <ChartEditorSidebar
          analysis={pendingAnalysis}
          appliedAnalysis={appliedAnalysis}
          originalAnalysisId={originalAnalysisId}
          dateRange={localDateRange}
          customDateStart={localCustomDateStart}
          customDateEnd={localCustomDateEnd}
          onDateRangeChange={handleDateRangeChange}
          onApply={handleApply}
          onClose={onClose}
        />
      </div>

      {/* Chart Content - uses appliedAnalysis for data fetching */}
      <div className="flex-1 min-h-0 overflow-hidden px-6 pb-6 relative">
        {shouldShowChart ? (
          <Chart
            analysisId={originalAnalysisId}
            workingAnalysis={appliedAnalysis}
            dateRange={localDateRange}
            customDateStart={localCustomDateStart}
            customDateEnd={localCustomDateEnd}
            isApplyingChanges={isApplying}
            onApplyChangesComplete={() => setIsApplying(false)}
            onDrillDown={handleDrillDown}
            onDimensionFilterNoRefresh={handleDimensionFilterNoRefresh}
            disableSave
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <p className="text-lg font-medium">Configure your chart</p>
              <p className="text-sm mt-1">Select metrics and dimensions, then click Apply to see results</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

